/**
 * list-query.ts — building list queries the API will actually accept.
 *
 * The two list pages each serialised something the endpoint does not declare,
 * and both failures were invisible in the UI (noten-g0x):
 *
 *   - Notifications joined multi-select filters into `status=PENDING,SENT`.
 *     `ListNotificationsQuerySchema` declares `status` and `channel` as single
 *     enums, so one selection worked and two produced a 400 — the filter
 *     appeared to break at random depending on how many chips were lit.
 *   - Templates sent `isActive`, which `ListTemplatesQuerySchema` does not
 *     declare at all, so the control did nothing and looked like it had.
 *
 * These helpers are pure so the contract can be pinned in a test rather than
 * rediscovered from a 400 in the network tab.
 */

/** Filters the notifications list endpoint declares, one value each. */
export interface NotificationListFilters {
  status?: string | null;
  channel?: string | null;
  eventType?: string;
  userId?: string;
  errorCategory?: string | null;
}

export function buildNotificationsQuery(
  filters: NotificationListFilters,
  page: number,
  limit: number,
): Record<string, unknown> {
  const query: Record<string, unknown> = { page, limit };
  if (filters.status) query.status = filters.status;
  if (filters.channel) query.channel = filters.channel;
  if (filters.eventType) query.eventType = filters.eventType;
  if (filters.userId) query.userId = filters.userId;
  if (filters.errorCategory) query.errorCategory = filters.errorCategory;
  return query;
}

/** Filters the templates list endpoint declares. `isActive` is NOT one of them. */
export interface TemplateListFilters {
  channel?: string | null;
  scope?: string;
  eventType?: string;
}

export function buildTemplatesQuery(
  filters: TemplateListFilters,
  page: number,
  limit: number,
  /** App del selector global; vacio = todas las apps. */
  appId = '',
): Record<string, unknown> {
  const query: Record<string, unknown> = { page, limit };
  // Sin appId el listado trae los templates de TODAS las apps mezclados, que
  // es lo que hacia parecer que "no estaban" los de un producto (estaban, en
  // otra app).
  if (appId) query.appId = appId;
  if (filters.channel) query.channel = filters.channel;
  if (filters.scope) query.scope = filters.scope;
  if (filters.eventType) query.eventType = filters.eventType;
  return query;
}

/**
 * Page count from the server's reported total.
 *
 * Templates used to guess with `data.length >= PAGE_SIZE ? page + 1 : page`,
 * which shows a next page that may be empty and hides the real size of the set.
 * When the server does report a total, use it.
 */
export function pageCountFrom(
  total: number | undefined,
  pageSize: number,
  currentPage: number,
): number {
  if (typeof total === 'number' && total >= 0) return Math.max(1, Math.ceil(total / pageSize));
  return Math.max(1, currentPage);
}
